import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useForm } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { useParams } from 'libs/router'
import { routes } from 'config/routes'
import { useUserRoles } from 'shared/hooks'
import { BackButton, Form, Header, HistoryButton, Panel } from 'shared/components'
import { useCouponValidationSchema } from 'features/coupons/hooks/useCouponValidationSchema'
import { CouponFormSections } from 'features/coupons/organisms/CouponForm/CouponFormSections'
import {
	CouponStatus,
	useCouponStatusLabel
} from 'features/coupons/molecules/CouponStatus/CouponStatus'
import { toCouponPayload } from 'features/coupons/utils/toCouponPayload'
import { useEditCouponMutation } from './mutations/useEditCouponMutation'
import { useFetchCoupon } from './queries/useFetchCoupon'
import { usePopulateCoupon } from './hooks/usePopulateCoupon'

const CouponPage = () => {
	const { t } = useTranslation()
	const params = useParams()
	const { isUser } = useUserRoles()
	const validationResolver = useCouponValidationSchema()
	const statusLabel = useCouponStatusLabel()

	const form = useForm({
		mode: 'onChange',
		resolver: validationResolver
	})
	const { handleSubmit, reset, setError } = form

	const { coupon } = useFetchCoupon()
	const couponData = coupon?.data
	const statusReasons = couponData?.status_reasons ?? []

	usePopulateCoupon({
		coupon: couponData,
		reset
	})

	const { editCouponMutation, isEditCouponLoading } = useEditCouponMutation({
		setError
	})

	const onSubmit = (data: any) => {
		editCouponMutation(toCouponPayload(data) as any)
	}

	const details = couponData
		? [
				{
					label: t('Usage'),
					value:
						couponData.max_uses == null
							? t('Used {{ used }} / unlimited', { used: couponData.times_used })
							: t('Used {{ used }} of {{ max }}', {
									used: couponData.times_used,
									max: couponData.max_uses
								})
				},
				...(statusReasons.length > 1
					? [{ label: t('Reasons'), value: statusReasons.map(statusLabel).join(', ') }]
					: []),
				...(couponData.created_by
					? [{ label: t('Created by'), value: couponData.created_by.email }]
					: [])
			]
		: []

	return (
		<>
			<Header
				title={t('Edit code')}
				eyebrow={t('Booking engine')}
				actions={
					<>
						<BackButton to={routes.couponIndex} text={t('Back to coupons')} />
						<HistoryButton to={routes.couponHistory(params.id)} />
					</>
				}
			/>
			<div className="mx-auto max-w-[704px] space-y-5 px-5 lg:px-0">
				{couponData && (
					<Panel
						title={t('Status')}
						icon={<InformationCircleIcon />}
						action={<CouponStatus status={couponData.status} />}>
						<dl className="space-y-2.5 text-[12px]">
							{details.map(({ label, value }) => (
								<div key={label} className="flex justify-between gap-3">
									<dt className="text-muted-foreground">{label}</dt>
									<dd className="text-right font-medium text-foreground">{value}</dd>
								</div>
							))}
						</dl>
						{statusReasons.length > 1 && statusReasons.includes('inactive') && (
							<p className="mt-3 text-[11px] text-muted-foreground">
								{t("Switching it on alone won't make it redeemable")}
							</p>
						)}
					</Panel>
				)}
				<Form
					variant="sections"
					submitText={t('Save changes')}
					cancelText={t('Discard changes')}
					onCancel={() => reset()}
					onSubmit={handleSubmit(onSubmit)}
					disabled={isUser}
					isLoading={isEditCouponLoading}>
					<CouponFormSections form={form} />
				</Form>
			</div>
		</>
	)
}

export default CouponPage
